import { IUser, refreshToken } from "@/api";
import { getProfile } from "@/api/profile";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import createAuthRefreshInterceptor, {
    AxiosAuthRefreshRequestConfig,
} from "axios-auth-refresh";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { advanceAuthSessionEpoch, AuthContext } from "./AuthContext";

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState<IUser | null>(null);
    const [mounted, setMounted] = useState(false);
    const queryClient = useQueryClient();

    const endAuthSession = useCallback(() => {
        advanceAuthSessionEpoch();
    }, []);

    // Read outside the state updater so a replacement is detected once, not
    // once per StrictMode re-invocation.
    const userRef = useRef<IUser | null>(null);
    const setPrincipal = useCallback(
        (next: IUser | null) => {
            const previous = userRef.current;
            if (previous && next && previous.id !== next.id) {
                endAuthSession();
            }
            userRef.current = next;
            setUser(next);
        },
        [endAuthSession]
    );

    // api
    const profileQuery = useQuery({
        queryKey: ["profile"],
        queryFn: getProfile,
        enabled: false,
        retry: false,
    });

    const refreshQuery = useQuery({
        queryKey: ["refreshProfile"],
        queryFn: getProfile,
        enabled: false,
        retry: false,
    });

    // Set up axios interceptors once on mount
    useEffect(() => {
        // Add request interceptor to attach Authorization header
        const requestInterceptor = axios.interceptors.request.use((request) => {
            const token = localStorage.getItem("access_token");
            if (token) {
                request.headers["Authorization"] = `Bearer ${token}`;
            }
            return request;
        });

        const handleAuthFailure = () => {
            endAuthSession();
            localStorage.removeItem("access_token");
            localStorage.removeItem("refresh_token");
            setIsAuthenticated(false);
            setPrincipal(null);
            window.location.href = "/login";
        };

        const refreshAuth = async (failedRequest: {
            response: { config: AxiosAuthRefreshRequestConfig };
        }) => {
            const refresh = localStorage.getItem("refresh_token");
            if (refresh) {
                try {
                    const response = await refreshToken(refresh);
                    const { access_token, refresh_token } = response;

                    localStorage.setItem("access_token", access_token);
                    localStorage.setItem("refresh_token", refresh_token);

                    if (failedRequest.response.config.headers) {
                        failedRequest.response.config.headers["Authorization"] =
                            `Bearer ${access_token}`;
                    }
                    return Promise.resolve();
                } catch (error) {
                    handleAuthFailure();
                    return Promise.reject(error);
                }
            } else {
                handleAuthFailure();
                return Promise.reject();
            }
        };

        const refreshInterceptor = createAuthRefreshInterceptor(
            axios,
            refreshAuth,
            { deduplicateRefresh: false }
        );

        // Both handlers close over this provider's setters, so a remount that
        // left them installed would keep advancing the epoch through the
        // previous instance.
        return () => {
            axios.interceptors.request.eject(requestInterceptor);
            axios.interceptors.response.eject(refreshInterceptor);
        };
    }, [endAuthSession, setPrincipal]);

    // handle profile
    useEffect(() => {
        if (!isAuthenticated) {
            if (profileQuery.isSuccess) {
                setPrincipal(profileQuery.data);
                setIsAuthenticated(true);
            }
        }
    }, [isAuthenticated, mounted, profileQuery, setPrincipal]);

    // initial check
    useEffect(() => {
        if (!mounted) {
            const token = localStorage.getItem("access_token");
            if (token) {
                profileQuery.refetch();
            } else {
                setIsAuthenticated(false);
                setMounted(true);
            }
        }
    }, [profileQuery, mounted]);

    const handleAuthentication = useCallback((authenticated: boolean) => {
        if (authenticated) {
            setIsAuthenticated(true);
        } else {
            setIsAuthenticated(false);
        }
        setMounted(true);
    }, []);

    useEffect(() => {
        if (profileQuery.isSuccess) {
            handleAuthentication(true);
        }
    }, [profileQuery, handleAuthentication]);

    // The success path needs no latch at all: setPrincipal already advances
    // the epoch only when the settled principal differs from the stored one,
    // so re-running it is idempotent and a same-principal re-settlement is
    // correctly silent.
    //
    // The error path has no principal to compare, so it latches on the error
    // instance instead. React-query keeps that reference stable for as long as
    // one failure stands, which is what stops the advance from re-triggering
    // its own re-render, and hands out a new one for the next failure.
    const handledErrorRef = useRef<unknown>(undefined);
    useEffect(() => {
        if (refreshQuery.isSuccess) {
            handledErrorRef.current = undefined;
            setPrincipal(refreshQuery.data);
            return;
        }
        if (refreshQuery.isError) {
            if (handledErrorRef.current === refreshQuery.error) return;
            handledErrorRef.current = refreshQuery.error;
            endAuthSession();
            setPrincipal(null);
            setIsAuthenticated(false);
            return;
        }
        handledErrorRef.current = undefined;
    }, [
        refreshQuery.isSuccess,
        refreshQuery.isError,
        refreshQuery.data,
        refreshQuery.error,
        endAuthSession,
        setPrincipal,
    ]);

    const createSession = useCallback(
        async (token: string, refresh_token: string) => {
            localStorage.setItem("access_token", token);
            localStorage.setItem("refresh_token", refresh_token);

            profileQuery.refetch();
        },
        [profileQuery]
    );

    const endSession = useCallback(() => {
        endAuthSession();
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("processing-scopes");
        setIsAuthenticated(false);
        setPrincipal(null);
        queryClient.clear();
    }, [queryClient, endAuthSession, setPrincipal]);

    const refreshSession = async () => {
        refreshQuery.refetch();
    };

    const session = {
        create: createSession,
        end: endSession,
        refresh: refreshSession,
    };

    const value = {
        authenticated: isAuthenticated,
        session,
        user,
    };

    if (!mounted) {
        return null;
    }

    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
};
